import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Search, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { api, fmtDate, inr } from "@/lib/api";
import { sourceLabel } from "@/lib/leads";
import { Empty, PageHeader, Panel } from "@/components/admin/common";
import { ContactButtons } from "@/components/admin/leads/LeadActions";

const HEAD = ["Customer", "Source", "Enquiries", "Bookings", "Total spent", "Packages", "Customer since", ""];

export default function Customers() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  useEffect(() => { api.get("/admin/customers").then((r) => setItems(r.data)).finally(() => setLoading(false)); }, []);
  const list = items.filter((c) => `${c.name} ${c.phone} ${c.email}`.toLowerCase().includes(q.toLowerCase()));
  const remove = async (c) => {
    if (!window.confirm(`Remove ${c.name} from Customers? Their leads and bookings stay.`)) return;
    await api.delete(`/admin/customers/${c.id}`);
    setItems((l) => l.filter((x) => x.id !== c.id));
    toast.success("Customer removed");
  };

  return (
    <div data-testid="admin-customers-page">
      <PageHeader title="Customers" subtitle="Confirmed leads converted from the CRM, plus everyone with a booking. Grouped by mobile number." />
      <Panel className="p-4 mb-4">
        <div className="relative md:w-80">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search customers" className="pl-9" data-testid="customer-search-input" />
        </div>
      </Panel>
      <Panel className="overflow-x-auto">
        {!loading && list.length === 0 ? <Empty testId="customers-empty">No customers yet. Convert a confirmed lead from its Lead Details page.</Empty> : (
          <Table className="min-w-[1000px]">
            <TableHeader><TableRow>{HEAD.map((h) => <TableHead key={h}>{h}</TableHead>)}</TableRow></TableHeader>
            <TableBody>
              {list.map((c) => (
                <TableRow key={c.id} data-testid={`customer-row-${c.id}`}>
                  <TableCell>
                    {c.lead_id ? <Link to={`/admin/leads/${c.lead_id}`} className="font-medium text-navy hover:text-ocean" data-testid={`customer-open-${c.id}`}>{c.name}</Link> : <p className="font-medium text-navy">{c.name}</p>}
                    <p className="text-xs text-slate-500">{c.phone}{c.email ? ` · ${c.email}` : ""}</p>
                  </TableCell>
                  <TableCell className="text-sm">{sourceLabel(c.source)}</TableCell>
                  <TableCell className="text-center font-mono">{c.enquiries}</TableCell>
                  <TableCell className="text-center font-mono">{c.bookings}</TableCell>
                  <TableCell className="font-mono">{inr(c.total_spent)}</TableCell>
                  <TableCell className="text-xs text-slate-600 max-w-[220px] truncate">{c.packages.join(", ") || "-"}</TableCell>
                  <TableCell className="text-sm text-slate-500">{fmtDate(c.created_at)}</TableCell>
                  <TableCell>
                    <div className="flex items-center justify-end gap-0.5">
                      <ContactButtons lead={c} id={`customer-${c.id}`} text={`Hi ${c.name}, greetings from Ocean Wings Travel!`} />
                      <button type="button" onClick={() => remove(c)} title="Remove from customers" data-testid={`customer-delete-${c.id}`} className="h-8 w-8 grid place-items-center rounded-lg text-rose-600 hover:bg-rose-50"><Trash2 className="h-4 w-4" /></button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Panel>
    </div>
  );
}
